<template>
  <div class="holiday-calendar">
      <!-- Calendar -->
      <header class="calendar-main__toolbar mb-2 rounded-xl border border-light">
        <div class="calendar-main__title">
          <span class="calendar-eyebrow">Navigasi Kalender</span>
          <h2 v-if="$refs.calendar">{{ $refs.calendar.title }}</h2>
          <h2 v-else>Jadwal Hari Libur</h2>
        </div>

        <div class="calendar-toolbar-actions">
          <button type="button" class="calendar-today-button" @click="setToday">
            <i class="far fa-calendar-check" aria-hidden="true"></i>
            Hari ini
          </button>
          <div class="calendar-nav-group" aria-label="Navigasi periode kalender">
            <button type="button" aria-label="Periode sebelumnya" @click="prev">
              <i class="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <button type="button" aria-label="Periode berikutnya" @click="next">
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
          </div>

          <v-menu bottom right offset-y>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="calendar-view-button"
                v-bind="attrs"
                v-on="on"
              >
                {{ typeToLabel[type] }}
                <i class="fas fa-chevron-down" aria-hidden="true"></i>
              </button>
            </template>
            <v-list class="calendar-view-menu">
              <v-list-item @click="type = 'day'">
                <v-list-item-title>Per Hari</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'week'">
                <v-list-item-title>Per Minggu</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = 'month'">
                <v-list-item-title>Per Bulan</v-list-item-title>
              </v-list-item>
              <v-list-item @click="type = '4day'">
                <v-list-item-title>Per 4 Hari</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </header>
      <data-calendar
          ref="calendar"
          v-model="value"
          :weekdays="weekday"
          :type="type"
          :events="events"
          :event-overlap-threshold="30"
          :event-color="getEventColor"
          @click:event="showEvent"
          @change="getEvents" />
        <v-menu
          v-model="selectedOpen"
          :close-on-content-click="false"
          absolute
          :position-x="popoverX"
          :position-y="popoverY"
          content-class="holiday-calendar-event-menu">
          <v-card
            class="holiday-calendar-event-card"
            :style="{ width: `${popoverWidth}px`, maxHeight: `${popoverMaxHeight}px` }"
            flat>
            <div class="holiday-event-card__header">
              <div class="holiday-event-card__icon" aria-hidden="true">
                <i class="far fa-calendar-times"></i>
              </div>
              <div class="holiday-event-card__heading">
                <span>Detail hari libur</span>
                <h3>{{ selectedEvent.name || 'Hari Libur' }}</h3>
              </div>
              <button
                type="button"
                class="holiday-event-card__close"
                aria-label="Tutup detail hari libur"
                @click="selectedOpen = false">
                <i class="fas fa-times" aria-hidden="true"></i>
              </button>
            </div>
            <v-card-text class="holiday-event-card__content">
              <div class="holiday-event-card__detail">
                <i class="far fa-calendar-alt" aria-hidden="true"></i>
                <div>
                  <span>Periode libur</span>
                  <strong>{{ selectedEvent.dateRange || '-' }}</strong>
                </div>
              </div>
              <div class="holiday-event-card__note">
                <span><i class="far fa-sticky-note" aria-hidden="true"></i> Keterangan</span>
                <p>{{ selectedEvent.notes || 'Tidak ada keterangan.' }}</p>
              </div>
            </v-card-text>
            <v-card-actions class="holiday-event-card__actions">
              <button
                type="button"
                class="holiday-event-card__dismiss"
                @click="selectedOpen = false">
                Tutup
              </button>
            </v-card-actions>
          </v-card>
        </v-menu>
  </div>
</template>

<script>

import module from '@/core/modules/CrudModule'
import DataCalendar from '@/component/common/DataCalendar.vue'

export default {
  components: { DataCalendar },
  props: {
    doctorId: {
      type: [String, Number],
      default: null,
    },
  },

  data: () => ({
    type: 'month',
    typeToLabel: {
      month: 'Per Bulan',
      week: 'Per Minggu',
      day: 'Per Hari',
      '4day': 'Per 4 Hari',
    },
    weekday: [0, 1, 2, 3, 4, 5, 6],
    value: '',
    events: [],
    colors: ['blue', 'indigo', 'deep-purple', 'cyan', 'green', 'orange', 'grey darken-1'],
    selectedEvent: {},
    selectedElement: null,
    selectedOpen: false,
    popoverX: 12,
    popoverY: 12,
    popoverWidth: 350,
    popoverMaxHeight: 560,
  }),

  methods: {

    async getHoliday() {
      const doctorId = this.doctorId == null ? '' : this.doctorId
      return await module.list(`holidays/get-calendar?doctor_id=${doctorId}`)
    },

    async getEvents() {
      const events = []
      const holidays = await this.getHoliday()
      const eventCount = holidays.length
      for (let i = 0; i < eventCount; i++) {
        events.push({
          name: holidays[i].name,
          start: new Date(holidays[i].start_date),
          end: holidays[i].end_date != "" ? new Date(holidays[i].end_date) : "",
          notes: holidays[i].notes,
          color: this.colors[this.random(0, this.colors.length - 1)],
          timed: true,
        })
      }
      this.events = events
    },

    showEvent({ nativeEvent, event }) {
      const open = () => {
        this.selectedEvent = event
        let startDate = new Date(event.start).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' }).replaceAll('/', '-')
        this.selectedEvent.details = `${event.name} Pada ${startDate}`
        this.selectedEvent.dateRange = startDate
        if (event.end != "") {
          let endDate = new Date(event.end).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' }).replaceAll('/', '-')
          this.selectedEvent.details += ` s/d ${endDate}`
          this.selectedEvent.dateRange += ` s/d ${endDate}`
        }
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight
        const calendarElement = this.$refs.calendar && this.$refs.calendar.$el
        const calendarRect = calendarElement
          ? calendarElement.getBoundingClientRect()
          : { left: 0, right: viewportWidth, top: 0, bottom: viewportHeight, width: viewportWidth, height: viewportHeight }
        const calendarPadding = 12
        const availableWidth = Math.max(
          120,
          Math.min(calendarRect.width - (calendarPadding * 2), viewportWidth - 24)
        )
        this.popoverMaxHeight = Math.max(
          180,
          Math.min(calendarRect.height - (calendarPadding * 2), viewportHeight - 24)
        )
        const popoverHeight = Math.min(330, this.popoverMaxHeight)

        this.popoverWidth = Math.min(350, availableWidth)

        const minX = Math.max(12, calendarRect.left + calendarPadding)
        const maxX = Math.max(
          minX,
          Math.min(
            viewportWidth - this.popoverWidth - 12,
            calendarRect.right - this.popoverWidth - calendarPadding
          )
        )
        const preferredX = nativeEvent.clientX - this.popoverWidth - 14
        this.popoverX = Math.min(maxX, Math.max(minX, preferredX))

        const minY = Math.max(12, calendarRect.top + calendarPadding)
        const maxY = Math.max(
          minY,
          Math.min(viewportHeight - popoverHeight - 12, calendarRect.bottom - popoverHeight - calendarPadding)
        )
        this.popoverY = Math.min(maxY, Math.max(minY, nativeEvent.clientY - 70))

        setTimeout(() => {
          this.selectedOpen = true
        }, 30)
      }
      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 30)
      } else {
        open()
      }
      nativeEvent.stopPropagation()
    },

    viewDay({ date }) {
      this.value = date
      this.type = 'day'
    },

    setToday() {
      this.value = ''
    },

    prev() {
      this.$refs.calendar.prev()
    },

    next() {
      this.$refs.calendar.next()
    },

    getEventColor(event) {
      return event.color
    },

    random(a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a
    },

  },

  watch: {
    doctorId() {
      this.getEvents()
    }
  },

  mounted() {
    this.$refs.calendar.scrollToTime('08:00')
  },

}
</script>

<style scoped>
.calendar-main__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  min-height: 74px;
  padding: 13px 17px;
  background: linear-gradient(110deg, rgba(20, 50, 99, 0.07), rgba(30, 149, 143, 0.06));
  border-bottom: 1px solid #E7EDF4;
}

.calendar-main__title h2 {
  font-size: 18px;
  margin: 0;
  color: #1A2433;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.calendar-eyebrow {
  display: block;
  margin-bottom: 3px;
  color: #1E958F;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.11em;
  line-height: 1.2;
  text-transform: uppercase;
}

.calendar-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 7px;
}

.calendar-today-button,
.calendar-view-button,
.calendar-nav-group button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  color: #143263;
  background: #FFFFFF;
  border: 1px solid #DCE5EF;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  transition: border-color 170ms ease, color 170ms ease, background 170ms ease, transform 170ms ease;
}

.calendar-today-button {
  gap: 7px;
  padding: 0 11px;
}

.calendar-today-button i {
  color: #1E958F;
}

.calendar-view-button {
  gap: 10px;
  min-width: 108px;
  padding: 0 11px;
}

.calendar-view-button i {
  font-size: 9px;
  color: #6B7280;
}

.calendar-nav-group {
  display: inline-flex;
  overflow: hidden;
  background: #FFFFFF;
  border: 1px solid #DCE5EF;
  border-radius: 10px;
}

.calendar-nav-group button {
  border: none;
  border-radius: 0;
  width: 38px;
  color: #6B7280;
}

.calendar-nav-group button:first-child {
  border-right: 1px solid #DCE5EF;
}

.calendar-today-button:hover,
.calendar-view-button:hover,
.calendar-nav-group button:hover {
  color: #1E958F;
  background: #F8FAFC;
  border-color: #cbd6e2;
}

.calendar-view-menu {
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(20, 50, 99, 0.14);
}

.holiday-calendar-event-card {
  display: flex !important;
  flex-direction: column;
  max-width: calc(100vw - 24px);
  overflow: hidden;
  border: 1px solid #E1E8F0;
  border-radius: 16px !important;
  background: #FFFFFF !important;
  box-shadow: 0 18px 42px rgba(20, 50, 99, 0.2) !important;
}

.holiday-event-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 16px 15px;
  background: linear-gradient(135deg, #F0FAF7 0%, #F8FBFD 100%);
  border-bottom: 1px solid #E4ECEF;
}

.holiday-event-card__icon {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: grid;
  place-items: center;
  color: #16867F;
  background: #FFFFFF;
  border: 1px solid #D7EAE6;
  border-radius: 12px;
  box-shadow: 0 5px 14px rgba(30, 149, 143, 0.1);
}

.holiday-event-card__heading {
  min-width: 0;
  flex: 1 1 auto;
}

.holiday-event-card__heading span {
  display: block;
  margin-bottom: 3px;
  color: #708397;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.holiday-event-card__heading h3 {
  margin: 0;
  overflow: hidden;
  color: #1A2433;
  font-size: 15px;
  font-weight: 750;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.holiday-event-card__close {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: grid;
  place-items: center;
  padding: 0;
  color: #637488;
  background: #FFFFFF;
  border: 1px solid #DCE6EC;
  border-radius: 10px;
  cursor: pointer;
}

.holiday-event-card__close:hover {
  color: #C43F4B;
  background: #FFF5F5;
  border-color: #F1C8CC;
}

.holiday-event-card__content {
  display: grid;
  gap: 14px;
  min-height: 0;
  overflow-y: auto;
  padding: 17px !important;
  color: #536174 !important;
}

.holiday-event-card__detail {
  display: flex;
  align-items: flex-start;
  gap: 11px;
}

.holiday-event-card__detail > i {
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  display: grid;
  place-items: center;
  color: #16867F;
  background: #EEF8F6;
  border-radius: 10px;
}

.holiday-event-card__detail div {
  min-width: 0;
}

.holiday-event-card__detail span,
.holiday-event-card__note > span {
  display: block;
  margin-bottom: 4px;
  color: #8493A4;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.holiday-event-card__detail strong {
  display: block;
  color: #263548;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.holiday-event-card__note {
  padding: 12px 13px;
  background: #F8FAFC;
  border: 1px solid #E8EDF3;
  border-radius: 12px;
}

.holiday-event-card__note > span i {
  margin-right: 5px;
  color: #16867F;
}

.holiday-event-card__note p {
  margin: 0;
  color: #536174;
  font-size: 12px;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.holiday-event-card__actions {
  flex: 0 0 auto;
  justify-content: flex-end;
  padding: 0 17px 17px !important;
}

.holiday-event-card__dismiss {
  min-width: 92px;
  height: 38px;
  padding: 0 18px;
  color: #FFFFFF;
  background: #14608F;
  border: 1px solid #14608F;
  border-radius: 10px;
  box-shadow: 0 6px 15px rgba(20, 96, 143, 0.2);
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
}

.holiday-event-card__dismiss:hover {
  background: #143263;
  border-color: #143263;
}

@media (max-width: 479.98px) {
  .holiday-event-card__header {
    padding: 14px;
  }

  .holiday-event-card__content {
    padding: 14px !important;
  }

  .holiday-event-card__actions {
    padding: 0 14px 14px !important;
  }

  .holiday-event-card__dismiss {
    width: 100%;
  }
}
</style>
